import { type RefObject, useLayoutEffect, useState } from 'react';

/**
 * Returns the nearest `<dialog>` (or `role="dialog"`) ancestor for use as a portal.
 * Skip the hook and pass a different ref directly as `container` only when you have a
 * reason to portal somewhere other than the dialog (e.g. a specific scroll container).
 *
 * The lookup happens in a layout effect so it works for popups that mount eagerly on first render.
 * For popups that mount lazily on user interaction (Popover, Tooltip, DropdownMenu), the effect's update
 * is a no-op on the first render and stable thereafter.
 *
 * @example
 * const triggerRef = useRef<HTMLButtonElement>(null);
 * const container = useDialogContainer(triggerRef);
 * <Tooltip>
 *   <TooltipTrigger ref={triggerRef}>Hover me</TooltipTrigger>
 *   <TooltipContent container={container}>Label</TooltipContent>
 * </Tooltip>
 */
export const useDialogContainer = (ref: RefObject<Element | null> | null | undefined): HTMLElement | undefined => {
  const [container, setContainer] = useState<HTMLElement | undefined>(undefined);

  useLayoutEffect(() => {
    const next = (ref?.current?.closest('dialog, [role="dialog"]') as HTMLElement | null) ?? undefined;
    setContainer((prev) => (prev === next ? prev : next));
  }, [ref]);

  return container;
};
